import {
  getFontCss,
  Popup,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectBulkDeactivatingFromState,
  selectSelectedVirtualCardListModels,
  selectUpdateableSelectedVirtualCardListModels,
} from 'src/app/VirtualCardsAllCards/redux/virtualCardsAllCards.selectors'
import CancelOrDeactivateVirtualCardsPopup from 'src/components/CancelOrDeactivateVirtualCardsPopup/CancelOrDeactivateVirtualCardsPopup'
import NotFoundPopupContent from 'src/components/NotFoundPopupContent/NotFoundPopupContent'
import SvgCloseCard from 'src/components/Svgs/SvgCloseCard'
import TestId from 'src/components/TestId/TestId'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import { CONFIRM_CLOSE_CARD_TEXT, ConfirmCloseCardText } from 'src/constants'
import { useFlyoutDispatch } from 'src/hooks'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { VirtualCardListModel } from 'src/models/virtualCard.models'
import { useBulkCloseVirtualCards } from 'src/queries/virtualCards.queries'
import { createSetIsBulkCloseVirtualCardsPopupOpen } from 'src/redux/actions/app.actions'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { css } from 'styled-components'

const BulkCloseVirtualCardsPopup: React.FC = () => {
  const translate = useTranslate()

  const selectedVirtualCards = useSelector(selectSelectedVirtualCardListModels)
  const updateableVirtualCards = useSelector(
    selectUpdateableSelectedVirtualCardListModels,
  )
  const locale = useSelector(selectNullSafeLocaleFromState)
  const bulkDeactivating = useSelector(selectBulkDeactivatingFromState)
  const { analytics } = useAnalyticsContext()

  const [confirmationText, setConfirmationText] = useState('')
  const [showConfirmationError, setShowConfirmationError] = useState(false)

  const dispatch = useFlyoutDispatch()
  const bulkCloseVirtualCards = useBulkCloseVirtualCards()

  const isBulkCloseCardFormValid =
    confirmationText === ConfirmCloseCardText[locale]

  const selectedCardTypesList = (
    selectedVirtualCards: VirtualCardListModel[],
  ): React.ReactElement[] => {
    let standardCount = 0
    let recurringCount = 0
    let standardGuestCount = 0
    let recurringGuestCount = 0
    const cardTypeArray = []

    selectedVirtualCards.map((vc) => {
      if (vc.recurs) {
        if (vc.direct) {
          recurringGuestCount++
        } else {
          recurringCount++
        }
      } else {
        if (vc.direct) {
          standardGuestCount++
        } else {
          standardCount++
        }
      }
    })

    if (standardCount > 0) {
      cardTypeArray.push(
        <li>
          <span
            key={'standard'}
            css={css`
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )}
            `}
          >
            {translateSingleString(locale, 'Standard (${num})', {
              num: standardCount.toString(),
            })}
          </span>
        </li>,
      )
    }
    if (recurringCount > 0) {
      cardTypeArray.push(
        <li>
          <span
            key={'recurring'}
            css={css`
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )}
            `}
          >
            {translateSingleString(locale, 'Recurring (${num})', {
              num: recurringCount.toString(),
            })}
          </span>
        </li>,
      )
    }
    if (standardGuestCount > 0) {
      cardTypeArray.push(
        <li>
          <span
            key={'standard_guest'}
            css={css`
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )}
            `}
          >
            {translateSingleString(locale, 'Guest (${num})', {
              num: standardGuestCount.toString(),
            })}
          </span>
        </li>,
      )
    }
    if (recurringGuestCount > 0) {
      cardTypeArray.push(
        <li>
          <span
            key={'recurring_guest'}
            css={css`
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )}
            `}
          >
            {translateSingleString(locale, 'Recurring Guest (${num})', {
              num: recurringGuestCount.toString(),
            })}
          </span>
        </li>,
      )
    }

    return cardTypeArray
  }

  const closeVirtualCards = (): void => {
    const closeableVirtualCardIds = updateableVirtualCards.map((vc) => vc.id)
    if (isBulkCloseCardFormValid) {
      bulkCloseVirtualCards.mutate({ virtualCardIds: closeableVirtualCardIds })
      analytics?.track('bulk vcn close - confirm', {
        bulk: true,
        number_of_cards: updateableVirtualCards.length,
      })
      setShowConfirmationError(false)
    } else {
      setShowConfirmationError(true)
    }
  }

  const canUpdateAllCards =
    updateableVirtualCards.length === selectedVirtualCards.length

  const modalWarningMessage = canUpdateAllCards
    ? '${pleaseNote}: This action will close your cards ${permanently}.'
    : '${pleaseNote}: Your card selection includes only cards that you are able to close. This applies to cards you have created or have access to as a budget user. This action will close your cards ${permanently}.'

  return (
    <>
      {updateableVirtualCards !== undefined &&
      updateableVirtualCards.length > 0 ? (
        <>
          <CancelOrDeactivateVirtualCardsPopup
            ariaLabel={translate('Close Virtual Cards')}
            title={
              <div
                css={css`
                  display: flex;
                  gap: 12px;
                  align-items: center;
                `}
              >
                {translate('Bulk Close Virtual Cards')}
              </div>
            }
            subtitle={
              <>
                <div
                  css={css`
                    margin-bottom: 4px;
                  `}
                >
                  {translate('Cards selected:')}
                </div>
                <ul
                  css={css`
                    margin-left: 30px;
                    margin-bottom: 20px;
                  `}
                >
                  {selectedCardTypesList(updateableVirtualCards)}
                </ul>
                {translate(modalWarningMessage, {
                  pleaseNote: (
                    <span
                      css={css`
                        ${getFontCss(
                          16,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.medium,
                        )}
                      `}
                    >
                      {translate('Please note')}
                    </span>
                  ),
                  permanently: (
                    <span
                      css={css`
                        ${getFontCss(
                          16,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.medium,
                        )}
                      `}
                    >
                      {translate('permanently')}
                    </span>
                  ),
                })}
                <TextInputField
                  css={css`
                    margin-top: 40px;
                  `}
                  allCaps
                  design={TextInputFieldDesigns.New}
                  name="remove-confirmation"
                  fieldLabel={
                    <span
                      css={css`
                        ${getFontCss(
                          16,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.medium,
                        )}
                      `}
                    >
                      {translate('Confirm by typing ${confirmString} below:', {
                        confirmString: (
                          <span
                            css={css`
                              ${getFontCss(
                                16,
                                styleConstants.colors.red,
                                styleConstants.fontFamilies.medium,
                              )}
                            `}
                          >
                            {translate(`"${CONFIRM_CLOSE_CARD_TEXT}"`)}
                          </span>
                        ),
                      })}
                    </span>
                  }
                  placeholder={translateSingleString(
                    locale,
                    'Please type "CLOSE"',
                  )}
                  onChange={(value: string): void => {
                    setConfirmationText(value.toUpperCase())
                    setShowConfirmationError(false)
                  }}
                />
                {showConfirmationError && (
                  <span
                    css={css`
                      ${getFontCss(
                        14,
                        styleConstants.colors.red,
                        styleConstants.fontFamilies.regular,
                      )}
                    `}
                  >
                    {translate('Confirmation text does not match')}
                  </span>
                )}
              </>
            }
            actionButtonLabel={
              <TestId
                css={css`
                  display: flex;
                  gap: 4px;
                  align-items: center;
                `}
                testId={'bulkCloseConfirmButton'}
              >
                <SvgCloseCard
                  css={css`
                    flex-grow: 1;
                  `}
                  fill={styleConstants.colors.white}
                />
                {translate('Confirm and Close Cards')}
              </TestId>
            }
            actionButtonLoading={bulkDeactivating}
            actionButtonOnClick={closeVirtualCards}
            cancelButtonLabel={translate('Cancel')}
            cancelButtonOnClick={(): void => {
              dispatch(createSetIsBulkCloseVirtualCardsPopupOpen(false))
              analytics?.track('bulk vcn close - cancel', {
                bulk: true,
                number_of_cards: updateableVirtualCards.length,
              })
            }}
          />
        </>
      ) : (
        <Popup
          onClickClose={(): void => {
            dispatch(createSetIsBulkCloseVirtualCardsPopupOpen(false))
          }}
          showCloseButton
        >
          <NotFoundPopupContent />
        </Popup>
      )}
    </>
  )
}

export default BulkCloseVirtualCardsPopup
